import * as React from "react";
import cloneDeep from "lodash/cloneDeep";

import { useState, useEffect, useRef } from "react";
import useMobileBreakpoint from "@src/hooks/useMobileBreakpoint";
import { mobileBreakpoint } from "../../../../entrypoints/theme";
import { ProductTile } from "@project-stories/ecommerce/Product/ProductTile";
import ProductTileSkeleton from "@project-stories/ecommerce/Product/ProductTileSkeleton";
import PromotionTile from "@project-stories/ecommerce/PromotionalTile/PromotionTile";
import Breadcrumbs from "../Product/Breadcrumbs";
import FiltersSidebar from "@src/stories/ecommerce/Collection/FiltersSidebar";
import CollectionToolbar from "@project-stories/ecommerce/Collection/CollectionToolbar";
import LoadingSpinner from "@src/stories/elements/Spinner/LoadingSpinner";
import QuickLinks from "@src/stories/ecommerce/Collection/QuickLinks";

export const Collection = ({ settings }) => {
  const [
    displayedProductsWithPromoBlocks,
    setDisplayedProductsWithPromoBlocks,
  ] = useState([]);
  const [products, setProducts] = useState([]);
  const [filters, setFilters] = useState([]);
  const isMobile = useMobileBreakpoint(mobileBreakpoint);
  const [isSidebarVisible, setSidebarVisible] = useState(false);

  const sortedPromotionBlocks = settings.promotion_blocks.sort((a, b) => {
    return b.sort_order - a.sort_order;
  });
  const promoBlocks = useRef(sortedPromotionBlocks);
  const paginationHaveLoaded = useRef(false);
  const isFiltering = useRef(false);


  const initialized = useRef(false);
  // const adjustedProductsPerPage = useRef(settings.products_per_page);


  // Ref and state for handling sticky toolbar
  const toolbarRef = useRef<HTMLDivElement>(null)
  const [isSticky, setIsSticky] = useState(false)

  useEffect(() => {
    const toolbar = toolbarRef.current
    if (!toolbar) return

    const calculateOffset = () => {
      const rect = toolbar.getBoundingClientRect()
      return rect.top + window.scrollY - 80
    }

    let stickyOffset = 0

    const init = () => {
      stickyOffset = calculateOffset()
    }

    const handleScroll = () => {
      setIsSticky(window.scrollY >= stickyOffset)
    }

    window.addEventListener("load", init)
    window.addEventListener("scroll", handleScroll, { passive: true })

    return () => {
      window.removeEventListener("load", init)
      window.removeEventListener("scroll", handleScroll)
    }
  }, [])

  const toggleSidebar = () => {
    setSidebarVisible((prevState) => {
      const newState = !prevState;
      document.body.classList.toggle("no-scroll__filters", newState);
      return newState;
    });
  };

  const {
    columns_mobile,
    columns_desktop,
    column_gap_mobile,
    column_gap_desktop,
    row_gap_mobile,
    row_gap_desktop,
    id,
    show_quick_action = false,
    action_type = 'add',
  } = settings.section;

  const getCurrentPage = () => {
    const params = new URLSearchParams(window.location.search);
    return Number(params.get("page")) || 1;
  };

  const handleProductsResults = (event) => {
    if (event.detail.type === "plp") {
      window.dispatchEvent(
        new CustomEvent("hide-spinner", {
          detail: {
            id: "spinner-collection",
          },
        }),
      );

      isFiltering.current = event.detail.isFiltering;

      if (isFiltering.current) {
        setProducts(event.detail.products);

        if (paginationHaveLoaded.current) {
          window.dispatchEvent(
            new CustomEvent("current-products-updated", {
              detail: {
                totalViewed: event.detail.products.length,
              },
            }),
          );
        } else {
          const intervalId = setInterval(() => {
            if (paginationHaveLoaded.current) {
              window.dispatchEvent(
                new CustomEvent("current-products-updated", {
                  detail: {
                    totalViewed: event.detail.products.length,
                  },
                }),
              );

              clearInterval(intervalId);
            }
          }, 100);
        }
      }

      const { products: productsToBeAdded, page } = event.detail;
      console.log("Received products for page", page, "with", productsToBeAdded.length, "products");

      if (!isFiltering.current) {
        isFiltering.current === false;
        const currentPageNumber = getCurrentPage();
        const totalProductsToShow = currentPageNumber * settings.products_per_page;

        const promoBlocksToInsert = getPromoBlocksToBeInserted(totalProductsToShow);

        const updatedDisplayedProductsWithPromoBlocks = insertPromoBlocks(
          productsToBeAdded,
          promoBlocksToInsert,
        );
        setDisplayedProductsWithPromoBlocks(
          updatedDisplayedProductsWithPromoBlocks,
        );

        if (paginationHaveLoaded.current) {
          window.dispatchEvent(
            new CustomEvent("current-products-updated", {
              detail: {
                totalViewed: productsToBeAdded.length,
              },
            }),
          );
        } else {
          const intervalId = setInterval(() => {
            if (paginationHaveLoaded.current) {
              window.dispatchEvent(
                new CustomEvent("current-products-updated", {
                  detail: {
                    totalViewed: productsToBeAdded.length,
                  },
                }),
              );

              clearInterval(intervalId);
            }
          }, 100);
        }
      }
    }
  };

  function handlePaginationLoad() {
    paginationHaveLoaded.current = true;
  }

  const handleSortResults = (event) => {
    // Sort functionality would be implemented here if needed
  };

  const handleFiltersResults = (event) => {
    // console.log('Collection handleFiltersResults received:', {
    //   type: event.detail.type,
    //   filtersCount: event.detail.filters?.length || 0,
    //   filters: event.detail.filters,
    //   colorFilter: event.detail.filters?.find(f => f.key === 'named_tags.swatch')
    // });
    
    if (event.detail.type === "plp") {
      if (event.detail.filters) {
        setFilters(event.detail.filters);
        // console.log('Updated filters state with', event.detail.filters.length, 'filters');
      }
    }
  };

  const handleEscapeKey = (event) => {
    if (event.key === "Escape") {
      setSidebarVisible(false);
    }
  };

  const handlePopState = () => {
    // Check if Alpine.js is already handling this popstate event
    const alpineHandling = (window as any).alpineHandlingPopstate;
    if (alpineHandling) return;
    
    // Set flag to prevent Alpine.js from also handling this
    (window as any).reactHandlingPopstate = true;
    setTimeout(() => {
      (window as any).reactHandlingPopstate = false;
    }, 1000);
    
    // Trigger a re-fetch when browser navigation occurs (back/forward buttons)
    const currentUrl = new URL(window.location.href);
    const hasFilters = currentUrl.searchParams.has('filters');
    const hasSortBy = currentUrl.searchParams.has('sort_by');
     
    // Dispatch URL change event with current parameters
    window.dispatchEvent(new CustomEvent("collection-url-changed", {
      detail: {
        url: window.location.href,
        pathname: window.location.pathname,
        search: window.location.search,
        hasFilters,
        hasSortBy
      }
    }));
  };

  const handleUrlChange = () => {
    // Check if Alpine.js is handling the popstate event
    if ((window as any).alpineHandlingPopstate)  return;
    
    // Add small delay to prevent race conditions with popstate events
    setTimeout(() => {
      // Parse current URL parameters
      const newUrl = new URL(window.location.href);
      const params = newUrl.searchParams;

      // Dispatch searchrequest event to trigger Alpine.js to reload collection
      window.dispatchEvent(new CustomEvent('searchrequest', {
        detail: {
          type: 'plp',
          index: params.get('sort_by') || (window as any).defaultSort || 'popularity',
          filters: params.get('filters') ? `collections:${(window as any).collectionHandle || settings.collection?.handle} AND ${params.get('filters')}` : `collections:${(window as any).collectionHandle || settings.collection?.handle}`,
          reset: true,
          source: 'react-collection-url-change'
        }
      }));
    }, 200);
  };

  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        document.querySelectorAll<HTMLElement>(
          "#product-grid .collection-tile, #product-grid .promotion-tile"
        ).forEach((el) => {
          el.style.opacity = "1";
          el.style.animation = "none";
        });
      }
    };

    window.addEventListener("pageshow", handlePageShow);

    return () => {
      window.removeEventListener("pageshow", handlePageShow);
    };
  }, []);

  useEffect(() => {
    if (!initialized.current) {
      initialized.current = true;

      window.dispatchEvent(new CustomEvent("collection-search-loaded"));

      window.addEventListener("pagination-have-loaded", handlePaginationLoad);
      window.addEventListener("product-list", handleProductsResults);
      window.addEventListener("filter-list", handleFiltersResults);
      window.addEventListener("keydown", handleEscapeKey);
      window.addEventListener("popstate", handlePopState);
      window.addEventListener("collection-url-changed", handleUrlChange);

      // Cleanup the event listeners on component unmount
      return () => {
        // window.removeEventListener("product-list", handleProductsResults);
        // window.removeEventListener("filter-list", handleFiltersResults);
        window.removeEventListener("keydown", handleEscapeKey);
        window.removeEventListener("popstate", handlePopState);
        window.removeEventListener("collection-url-changed", handleUrlChange);
      };
    }
  }, []);

  const getProductsFromLoadedProducts = (products, nextPageCount) => {
    return products.slice(0, nextPageCount);
  };

  const insertPromoBlocks = (productsToBeAdded, promoBlocksToInsert) => {
    const finalGrid: any[] = [];
    let productIndex = 0;

    const maxPosition = Math.max(
      productsToBeAdded.length - 1,
      ...promoBlocksToInsert.map(pb => pb.position)
    );

    for (let gridIndex = 0; gridIndex <= maxPosition; gridIndex++) {
      const promoAtPosition = promoBlocksToInsert.find(pb => pb.position === gridIndex);
      
      if (promoAtPosition) {
        finalGrid.push(promoAtPosition.block);
      } else if (productIndex < productsToBeAdded.length) {
        finalGrid.push(productsToBeAdded[productIndex]);
        productIndex++;
      }
    }
    
    return finalGrid;
  };

  const getPromoBlocksToBeInserted = (totalProductsDisplayed) => {
    const blocksToInsert: any = [];

    promoBlocks.current.forEach((block) => {
      // Ensure block.position is an array before processing
      const positions = Array.isArray(block.position) ? block.position : [block.position].filter(Boolean);
      
      positions.sort((a, b) => a - b);
      positions.forEach((position) => {
        if (position <= totalProductsDisplayed + blocksToInsert.length + 1) {
          blocksToInsert.push({
            position: position - 1,
            block,
          });
        }
      });
    });

    return blocksToInsert.sort((a, b) => a.position - b.position);
  };

  // Generate the column span class for mobile
  const getColumnSpanClass = (promotion) => {
      const mobileSpan = promotion.col_span_mobile || 1;
      const columnClass = mobileSpan > 1 ? `span-${mobileSpan}` : '';
      
      return columnClass;
  };

  const currentPage = getCurrentPage();
  const totalProductsToShow = settings.products_per_page * currentPage || 12;
  const skeletonCount = totalProductsToShow + getPromoBlocksToBeInserted(totalProductsToShow).length;

  return (
    <div x-init={`initialise('${settings.collection.handle}', true, ${settings.products_per_page})`}>
      <QuickLinks list={settings.quick_links} collectionHandle={settings.collection.handle} settings={settings.section.show_quick_links} />
      <FiltersSidebar
        isVisible={isSidebarVisible}
        filters={filters}
        metaobject_filters={settings.filters}
        toggleSidebar={toggleSidebar}
        mode={settings.section.default_filter_view}
      />
      <div ref={toolbarRef} className={`flex items-center justify-between mb-4 collection__toolbar ${settings.section.sticky_filter_bar ? 'sticky top-[67.5px] lg:top-20 z-10 bg-white shadow-[0_-1px_0_white]' : ''} `} >
        {!isMobile && !isSticky && (
          <Breadcrumbs
            templateName={settings.template.name}
            shop={settings.shop}
            collection={settings.collection}
            product={null}
            routes={settings.routes}
            blog={null}
            article={null}
            page={settings.page}
            request={null}
            pageTitle={null}
          />
        )}
        <CollectionToolbar mode={settings.section.default_filter_view} toggleSidebar={toggleSidebar} />
      </div>
      <div
        id="product-grid"
        className={`collection__product-grid relative grid grid-cols-${columns_mobile} lg:grid-cols-${columns_desktop} gap-x-[${column_gap_mobile}px] lg:gap-x-[${column_gap_desktop}px] gap-y-[${row_gap_mobile}px] lg:gap-y-[${row_gap_desktop}px] auto-cols-max`}
        data-id={id}
      >

        <LoadingSpinner id="spinner-collection" />

        {/* Render skeleton placeholders while initial products are loading */}
        {displayedProductsWithPromoBlocks.length === 0 && products.length === 0 && !isFiltering.current && (
          Array.from({ length: skeletonCount }).map((_, i) => (
            <ProductTileSkeleton key={`skeleton-${i}`} />
          ))
        )}

        {isFiltering.current ? (
          <>
            {products.map((block, index) => (
              <React.Fragment key={`filtered-${block.id || index}`}>
                <div className={"collection-tile"} style={{ opacity: 0, animation: 'fadeIn 0.3s ease-in-out forwards' }}>
                  <ProductTile product={block} siblings={[]} show_quick_action={show_quick_action} action_type={action_type} />
                </div>
              </React.Fragment>
            ))}
          </>
        ) : (
          <>
            {displayedProductsWithPromoBlocks.map((block, index) => (
              <React.Fragment key={`grid-${block.id || block.handle || index}`}>
                {block.type === "promo" ? (
                  <div style={{ opacity: 0, animation: 'fadeIn 0.3s ease-in-out forwards', animationDelay: `${index + 10}ms` }} className={`promotion-tile ${getColumnSpanClass(block)}`}>
                    <PromotionTile promotion={block} sectionSettings={settings.section} />
                  </div>
                ) : (
                  <div className={"collection-tile"} style={{ opacity: 0, animation: 'fadeIn 0.3s ease-in-out forwards', animationDelay: `${index + 10}ms` }}>
                    <ProductTile product={block} siblings={[]} />
                  </div>
                )}
              </React.Fragment>
            ))}
          </>
        )}
      </div>
    </div>
  );
};

export default Collection;
